import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, LogOut, FileText, CheckCircle } from 'lucide-react';

// Frame Interventions Terms of Use - Full Text
const TERMS_SECTIONS = [
  {
    title: "1. Purpose and Scope",
    content: `Frame Interventions is part of the FrameEd platform and is provided to support school-based intervention tracking, MTSS documentation, progress monitoring, reporting, and related educational workflows.

Access is limited to authorized users and authorized school-related purposes.`
  },
  {
    title: "2. Authorized Use",
    content: `Users may use the platform only for legitimate school, educational, administrative, intervention, documentation, and evaluation-support purposes.

Users may not misuse the platform, attempt unauthorized access, interfere with platform operations, or use the platform to develop or support a competing product or service.`
  },
  {
    title: "3. Platform Ownership and Intellectual Property",
    content: `FrameEd, Frame Interventions, and all related software, workflows, reports, templates, dashboards, interfaces, designs, prompts, logic, documentation, names, branding, and future improvements remain the property of the platform owner, Peter O'Rourke or assigned affiliated entity.

Access to the platform does not transfer ownership or grant rights beyond authorized use.

Users may not copy, reproduce, reverse engineer, distribute, resell, sublicense, create derivative works from, or remove proprietary notices from the platform.`
  },
  {
    title: "4. School and Student Data",
    content: `The school or authorized organization retains ownership of data entered by its authorized users.

The platform may host, process, store, transmit, back up, and use data as needed to provide, maintain, secure, support, and improve the service.

Users are responsible for entering only information they are authorized to enter under applicable school policy and law.`
  },
  {
    title: "5. Student Privacy and Confidentiality",
    content: `Users must handle student information responsibly and in accordance with applicable student privacy, education record, confidentiality, and school data policies.

The platform may support FERPA-related educational workflows, but users and schools remain responsible for determining what information may be entered, who may access it, and whether any parent notice, consent, district approval, or data privacy agreement is required.`
  },
  {
    title: "6. De-Identified and Aggregated Data",
    content: `The platform owner may use de-identified, anonymized, or aggregated data for product improvement, analytics, benchmarking, research, development, security, and system administration, provided the data does not identify an individual student, staff member, or school.`
  },
  {
    title: "7. Feedback",
    content: `If users provide suggestions, ideas, comments, bug reports, feature requests, or other feedback, the platform owner may use that feedback without restriction, payment, credit, or obligation.

Any improvements based on feedback remain part of the platform.`
  },
  {
    title: "8. Confidentiality of Platform Information",
    content: `Users may not share nonpublic platform information, screenshots, workflows, prompts, reports, internal tools, pricing, technical details, or roadmap information with unauthorized third parties without written permission, except as required by law or approved school process.`
  },
  {
    title: "9. Account Security",
    content: `Users are responsible for keeping login credentials confidential and for promptly reporting suspected unauthorized access, misuse, or security concerns.

Accounts may not be shared.

The platform owner may suspend or restrict access if misuse, unauthorized access, or a security concern is suspected.`
  },
  {
    title: "10. Educational and Legal Decision Support",
    content: `The platform is intended to support documentation, organization, analysis, and team decision-making.

The platform does not replace professional judgment, team decisions, school policy, legal obligations, or evaluation requirements.

The platform does not determine special education eligibility, diagnose disabilities, or make final educational placement decisions.

Any AI-generated or system-generated content must be reviewed by qualified school personnel before being used in official records, communications, or decisions.`
  },
  {
    title: "11. Pilot, Trial, Beta, and Feature Availability",
    content: `Some features may be pilot, trial, beta, incomplete, modified, renamed, interrupted, discontinued, or changed over time.

Access to the platform or any feature is not guaranteed indefinitely unless covered by a separate written agreement.`
  },
  {
    title: "12. Support",
    content: `Support, onboarding, training, and troubleshooting may be provided on a reasonable-efforts basis unless a separate written agreement states otherwise.`
  },
  {
    title: "13. Disclaimer of Warranties",
    content: `The platform is provided "as is" and "as available" to the maximum extent permitted by law.

No warranty is made that the platform will be uninterrupted, error-free, fully accurate, or fit for every particular purpose.`
  },
  {
    title: "14. Limitation of Liability",
    content: `To the maximum extent permitted by law, the platform owner is not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of data, revenue, profits, opportunity, or goodwill arising from use of the platform.

If no fees were paid, total liability shall be limited to one hundred dollars ($100.00), unless a separate written agreement states otherwise or applicable law requires otherwise.`
  },
  {
    title: "15. Suspension or Termination",
    content: `Access may be suspended, restricted, or terminated if a user or organization violates the terms, misuses the platform, creates a security concern, or no longer has authorized access.

Upon termination, users must stop using the platform.`
  },
  {
    title: "16. Governing Law",
    content: `These terms are governed by the laws of the State of Arizona, unless a separate written agreement states otherwise.`
  },
  {
    title: "17. Electronic Acceptance",
    content: `By checking the agreement box and clicking Accept and Continue, the user acknowledges that they have read, understood, and agreed to the terms.

The user agrees that electronic acceptance has the same effect as a written signature for purposes of platform access.`
  }
];

export default function TermsPage() {
  const navigate = useNavigate();
  const { logout, user, refreshUser } = useAuth();
  const [termsInfo, setTermsInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    loadTermsInfo();
  }, []);

  const loadTermsInfo = async () => {
    try {
      const res = await apiService.getCurrentTerms();
      setTermsInfo(res.data);
      
      // If already accepted, redirect to dashboard
      if (res.data.user_has_accepted) {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to load terms info:', error);
      toast.error('Failed to load terms information');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!agreed) {
      toast.error('Please check the agreement box to continue');
      return;
    }

    setAccepting(true);
    try {
      await apiService.acceptTerms({
        terms_version: termsInfo.version,
        confirmed: true
      });
      
      toast.success('Terms accepted successfully');
      
      // Refresh user data to update terms_accepted status
      if (refreshUser) {
        await refreshUser();
      }
      
      // Navigate to dashboard
      navigate('/');
    } catch (error) {
      console.error('Failed to accept terms:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept terms');
    } finally {
      setAccepting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
      </div>
    );
  }

  // If already accepted (shouldn't normally reach here due to redirect)
  if (termsInfo?.user_has_accepted) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#1C1C1C] mb-2">Terms Already Accepted</h2>
          <p className="text-[#595959] mb-4">
            You have already accepted the current terms of use.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="terms-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
              <span className="text-white font-bold text-lg">F</span>
            </div>
            <div>
              <h1 className="font-semibold text-[#1C1C1C]">Frame Interventions</h1>
              <p className="text-xs text-[#595959]">MTSS Progress Tracker</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-[#595959] hover:text-[#1C1C1C] transition-colors"
            data-testid="terms-logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2C4C3B]/10 mb-4">
            <FileText className="h-8 w-8 text-[#2C4C3B]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1C1C1C] mb-2">
            Frame Interventions Terms of Use
          </h1>
          <p className="text-[#595959] max-w-2xl mx-auto">
            Please review and accept these terms before using Frame Interventions.
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 mb-6">
          <p className="text-[#595959] leading-relaxed">
            To continue using Frame Interventions, please review and accept the terms below. 
            These terms help protect student information, clarify responsible platform use, 
            and explain the role of FrameEd as a support tool for school teams.
          </p>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-xl border border-[#E5E5E5] mb-6">
          <div 
            className="max-h-[400px] overflow-y-auto p-6 space-y-6"
            data-testid="terms-content"
          >
            {TERMS_SECTIONS.map((section, index) => (
              <div key={index} className="pb-4 border-b border-[#E5E5E5] last:border-0 last:pb-0">
                <h3 className="font-semibold text-[#1C1C1C] mb-2">{section.title}</h3>
                <p className="text-sm text-[#595959] whitespace-pre-line leading-relaxed">
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Agreement Section */}
        <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 mb-6">
          <div className="flex items-start gap-3">
            <Checkbox
              id="terms-agreement"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-1 data-[state=checked]:bg-[#2C4C3B] data-[state=checked]:border-[#2C4C3B]"
              data-testid="terms-checkbox"
            />
            <label 
              htmlFor="terms-agreement" 
              className="text-sm text-[#1C1C1C] cursor-pointer leading-relaxed"
            >
              I have read and agree to the Frame Interventions Terms of Use, including the terms 
              regarding platform access, student data, confidentiality, privacy, and responsible use.
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Button
            onClick={handleAccept}
            disabled={!agreed || accepting}
            className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="terms-accept-btn"
          >
            {accepting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              'Accept and Continue'
            )}
          </Button>
        </div>

        {/* Footer Note */}
        <div className="text-center">
          <p className="text-xs text-[#A3A3A3]">
            If your school or organization has a separate written agreement with FrameEd, 
            that agreement may also apply.
          </p>
          {termsInfo && (
            <p className="text-xs text-[#A3A3A3] mt-2">
              Version: {termsInfo.version} • Effective: {termsInfo.effective_date}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
